const PREVIEW_CONFIG = Object.freeze({ segmentSeconds: 15 });
const select = (selector, root = document) => root.querySelector(selector);
const selectAll = (selector, root = document) => [...root.querySelectorAll(selector)];

function refreshIcons() { window.lucide?.createIcons(); }
function trackEvent(event, details = {}) {
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({ event, ...details });
}
function setIcon(button, name, label) {
  if (!button) return;
  button.setAttribute("aria-label", label);
  button.title = label;
  button.innerHTML = '<i data-lucide="' + name + '" aria-hidden="true"></i>';
  refreshIcons();
}
function setupHeaderOffset() {
  const header = select("[data-header]");
  const update = () => document.documentElement.style.setProperty("--header-offset", Math.ceil(header.getBoundingClientRect().height) + "px");
  update();
  if ("ResizeObserver" in window) new ResizeObserver(update).observe(header);
  else window.addEventListener("resize", update);
}
function setupMobileMenu() {
  const toggle = select("[data-menu-toggle]");
  const menu = select("[data-mobile-menu]");
  const backdrop = select("[data-menu-backdrop]");
  const background = selectAll("main, .site-footer, .announcement, .mobile-buy-bar, .site-header > .brand");
  let backgroundState = [];
  let isOpen = false;
  const focusables = () => [toggle, ...selectAll("a[href], button:not(:disabled)", menu)];
  const setMenu = (open, restoreFocus = true) => {
    if (open === isOpen) return;
    isOpen = open;
    toggle.setAttribute("aria-expanded", String(open));
    setIcon(toggle, open ? "x" : "menu", open ? "Close menu" : "Open menu");
    menu.hidden = !open;
    backdrop.hidden = !open;
    document.body.classList.toggle("is-menu-open", open);
    if (open) {
      backgroundState = background.map(element => ({ element, inert: element.inert }));
      background.forEach(element => { element.inert = true; });
      select("a", menu)?.focus({ preventScroll: true });
      trackEvent("menu_open");
    } else {
      backgroundState.forEach(({ element, inert }) => { element.inert = inert; });
      if (restoreFocus) toggle.focus({ preventScroll: true });
    }
    document.dispatchEvent(new Event("artpanda:menu"));
  };
  toggle.addEventListener("click", () => setMenu(!isOpen));
  backdrop.addEventListener("click", () => setMenu(false));
  selectAll("a", menu).forEach(link => link.addEventListener("click", () => setMenu(false, false)));
  document.addEventListener("keydown", event => {
    if (!isOpen) return;
    if (event.key === "Escape") {
      event.preventDefault();
      setMenu(false);
    } else if (event.key === "Tab") {
      const items = focusables();
      const index = items.indexOf(document.activeElement);
      if (index === -1 || (event.shiftKey && index === 0) || (!event.shiftKey && index === items.length - 1)) {
        event.preventDefault();
        (event.shiftKey ? items[items.length - 1] : items[0]).focus();
      }
    }
  });
  document.addEventListener("focusin", event => {
    if (isOpen && event.target !== toggle && !menu.contains(event.target)) select("a", menu)?.focus();
  });
  window.addEventListener("resize", () => {
    if (innerWidth > 720 && isOpen) setMenu(false, false);
  });
}
function setupDemoExperience() {
  const root = select("[data-demo-experience]");
  const video = select("[data-demo-video]", root);
  const player = select("[data-demo-player]", root);
  const stage = select("[data-demo-stage]", root);
  const poster = select("[data-demo-stage-poster]", root);
  const tabs = selectAll("[data-demo-select]", root);
  const rail = select(".demo-selector", root);
  const counter = select("[data-demo-counter]", root);
  const kicker = select("[data-demo-kicker]", root);
  const title = select("[data-demo-title]", root);
  const play = select("[data-demo-play]", root);
  const sound = select("[data-demo-sound]", root);
  const seek = select("[data-demo-seek]", root);
  const time = select("[data-demo-time]", root);
  const fullscreen = select("[data-demo-fullscreen]", root);
  const feedback = select("[data-demo-feedback]", root);
  const motion = matchMedia("(prefers-reduced-motion: reduce)");
  let activeIndex = 0;
  let generation = 0;
  let visible = false;
  let switching = false;
  let userPaused = motion.matches;
  let scrubbing = false;
  let resumeAfterSeek = false;
  let failed = false;
  const duration = () => Math.min(Number.isFinite(video.duration) ? video.duration : PREVIEW_CONFIG.segmentSeconds, PREVIEW_CONFIG.segmentSeconds);
  const formatTime = seconds => Math.floor(seconds / 60) + ":" + String(Math.floor(seconds % 60)).padStart(2, "0");
  const showPoster = () => stage.classList.remove("is-media-ready");
  const updatePlay = () => setIcon(play, video.paused || video.ended ? "play" : "pause", video.paused || video.ended ? "Play video" : "Pause video");
  const updateSound = () => setIcon(sound, video.muted ? "volume-x" : "volume-2", video.muted ? "Turn sound on" : "Mute video");
  const updateTimeline = () => {
    const total = duration();
    const elapsed = Math.min(video.currentTime || 0, total);
    seek.max = String(total);
    seek.disabled = failed || video.readyState < 1;
    if (!scrubbing) seek.value = String(elapsed);
    seek.setAttribute("aria-valuetext", formatTime(elapsed) + " of " + formatTime(total));
    time.textContent = formatTime(elapsed) + " / " + formatTime(total);
  };
  const revealFrame = () => {
    const expected = generation;
    const reveal = () => {
      if (generation === expected && video.readyState >= 2 && !failed) stage.classList.add("is-media-ready");
    };
    if (video.requestVideoFrameCallback) video.requestVideoFrameCallback(reveal);
    else requestAnimationFrame(reveal);
  };
  const playSelected = () => {
    if (visible && !userPaused && !scrubbing && !failed && !document.hidden) video.play().then(updatePlay).catch(updatePlay);
  };
  const activate = (index, userInitiated = false) => {
    activeIndex = (index + tabs.length) % tabs.length;
    const tab = tabs[activeIndex];
    generation += 1;
    switching = true;
    scrubbing = false;
    failed = false;
    if (userInitiated) userPaused = false;
    feedback.hidden = true;
    showPoster();
    video.pause();
    video.poster = tab.dataset.poster || "";
    poster.src = video.poster;
    video.src = tab.dataset.src || "";
    video.setAttribute("aria-label", tab.dataset.title || "Artpanda Y02 demo");
    stage.classList.toggle("is-portrait", tab.dataset.orientation === "portrait");
    counter.textContent = String(activeIndex + 1).padStart(2, "0") + " / " + String(tabs.length).padStart(2, "0");
    kicker.textContent = tab.dataset.kicker || "";
    title.textContent = tab.dataset.title || "";
    tabs.forEach((item, index) => {
      item.classList.toggle("is-active", index === activeIndex);
      item.setAttribute("aria-pressed", String(index === activeIndex));
    });
    // Move only the thumbnail rail; scrollIntoView would move the page too.
    if (rail.scrollWidth > rail.clientWidth) {
      const left = tab.getBoundingClientRect().left - rail.getBoundingClientRect().left + rail.scrollLeft;
      rail.scrollTo({ left, behavior: motion.matches ? "auto" : "smooth" });
    }
    updatePlay();
    updateTimeline();
    video.load();
    if (userInitiated) trackEvent("demo_select", { demo_index: activeIndex + 1 });
  };
  const finish = () => {
    if (switching || scrubbing || (!video.ended && video.currentTime < duration())) return;
    if (!motion.matches && !userPaused && visible && !document.hidden) activate(activeIndex + 1);
    else { userPaused = true; video.pause(); updatePlay(); }
  };
  tabs.forEach((tab, index) => tab.addEventListener("click", () => activate(index, true)));
  play.addEventListener("click", () => {
    if (failed) { activate(activeIndex, true); return; }
    if (video.paused || video.ended) {
      userPaused = false;
      if (video.ended || video.currentTime >= duration()) video.currentTime = 0;
      video.play().then(updatePlay).catch(updatePlay);
    } else { userPaused = true; video.pause(); }
  });
  sound.addEventListener("click", () => { video.muted = !video.muted; updateSound(); });
  seek.addEventListener("input", () => {
    if (seek.disabled) return;
    if (!scrubbing) resumeAfterSeek = !video.paused;
    scrubbing = true;
    video.pause();
    video.currentTime = Math.max(0, Math.min(Number(seek.value), duration()));
    updateTimeline();
  });
  seek.addEventListener("change", () => {
    if (!scrubbing) return;
    scrubbing = false;
    if (video.currentTime >= duration() - 0.05) userPaused = true;
    else if (resumeAfterSeek) playSelected();
    updateTimeline();
  });
  if (!player.requestFullscreen && !video.webkitEnterFullscreen) fullscreen.hidden = true;
  fullscreen.addEventListener("click", async () => {
    try {
      if (document.fullscreenElement === player) await document.exitFullscreen();
      else if (player.requestFullscreen) await player.requestFullscreen();
      else if (video.webkitEnterFullscreen) video.webkitEnterFullscreen();
    } catch { fullscreen.title = "Full screen is not available in this browser"; }
  });
  document.addEventListener("fullscreenchange", () => {
    const active = document.fullscreenElement === player;
    setIcon(fullscreen, active ? "minimize" : "maximize", active ? "Exit full screen" : "Enter full screen");
    if (!active && player.contains(document.activeElement)) fullscreen.focus({ preventScroll: true });
  });
  select("[data-demo-retry]", root).addEventListener("click", () => activate(activeIndex, true));
  video.addEventListener("error", () => {
    failed = true;
    switching = false;
    showPoster();
    feedback.hidden = false;
    updateTimeline();
    updatePlay();
  });
  video.addEventListener("loadedmetadata", () => { switching = false; updateTimeline(); playSelected(); });
  video.addEventListener("loadeddata", revealFrame);
  video.addEventListener("seeked", revealFrame);
  video.addEventListener("playing", revealFrame);
  video.addEventListener("play", updatePlay);
  video.addEventListener("pause", updatePlay);
  video.addEventListener("timeupdate", () => {
    updateTimeline();
    if (video.currentTime >= duration() && !video.paused) finish();
  });
  video.addEventListener("ended", finish);
  if ("IntersectionObserver" in window) {
    new IntersectionObserver(([entry]) => {
      visible = entry.isIntersecting && entry.intersectionRatio >= 0.25;
      if (visible) playSelected();
      else video.pause();
    }, { threshold: [0, 0.25, 0.7] }).observe(stage);
  } else { visible = true; playSelected(); }
  document.addEventListener("visibilitychange", () => document.hidden ? video.pause() : playSelected());
  const handleMotion = () => { if (motion.matches) { userPaused = true; video.pause(); } };
  if (motion.addEventListener) motion.addEventListener("change", handleMotion);
  else motion.addListener(handleMotion);
  selectAll("[data-demo-jump]").forEach(link => link.addEventListener("click", event => {
    event.preventDefault();
    const index = Number(link.dataset.demoJump || 0);
    activate(Number.isFinite(index) ? index : 0, true);
    if (location.hash !== "#demos") history.pushState(null, "", "#demos");
    select("#demos").scrollIntoView({ behavior: motion.matches ? "auto" : "smooth", block: "start" });
  }));
  updatePlay();
  updateSound();
  updateTimeline();
}
function setupProductGallery() {
  const image = select("[data-product-image]");
  const thumbs = selectAll("[data-product-thumb]");
  let request = 0;
  thumbs.forEach(thumb => thumb.addEventListener("click", async () => {
    const next = thumb.dataset.imageSrc;
    const current = ++request;
    if (!next || image.getAttribute("src") === next) return;
    const candidate = new Image();
    candidate.src = next;
    try { await candidate.decode(); } catch { return; }
    if (current !== request) return;
    image.src = next;
    image.alt = thumb.dataset.imageAlt || "Artpanda Y02";
    thumbs.forEach(item => {
      item.classList.toggle("is-active", item === thumb);
      item.setAttribute("aria-pressed", String(item === thumb));
    });
  }));
}
function setupReveal() {
  if (!("IntersectionObserver" in window) || matchMedia("(prefers-reduced-motion: reduce)").matches) return;
  const observer = new IntersectionObserver(entries => entries.forEach(entry => {
    if (!entry.isIntersecting) return;
    entry.target.classList.add("is-visible");
    observer.unobserve(entry.target);
  }), { rootMargin: "0px 0px -4% 0px", threshold: 0.04 });
  document.documentElement.classList.add("motion-ready");
  selectAll("[data-reveal]").forEach(item => observer.observe(item));
}
function setupMobileActionBar() {
  const bar = select("[data-mobile-buy-bar]");
  const hero = select(".hero");
  const support = select("#support");
  const ctas = selectAll(".hero [data-action-link], .purchase-cta, .final-cta [data-action-link]");
  let ticking = false;
  const visible = element => {
    const rect = element.getBoundingClientRect();
    return rect.height > 0 && rect.bottom > 80 && rect.top < innerHeight - 16;
  };
  const update = () => {
    ticking = false;
    const show = innerWidth <= 720 && !document.body.classList.contains("is-menu-open") && hero.getBoundingClientRect().bottom < 0 && !visible(support) && !ctas.some(visible);
    bar.hidden = !show;
    bar.inert = !show;
    bar.classList.toggle("is-visible", show);
  };
  const schedule = () => { if (!ticking) { ticking = true; requestAnimationFrame(update); } };
  addEventListener("scroll", schedule, { passive: true });
  addEventListener("resize", schedule);
  document.addEventListener("artpanda:menu", schedule);
  update();
}
function setupContact() {
  const button = select("[data-copy-email]");
  const email = select("[data-support-email]");
  const feedback = select("[data-contact-feedback]");
  button.addEventListener("click", async () => {
    const address = email.textContent.trim();
    let copied = false;
    try { await navigator.clipboard.writeText(address); copied = true; }
    catch {
      const field = document.createElement("textarea");
      field.value = address;
      field.className = "clipboard-fallback";
      field.setAttribute("readonly", "");
      document.body.append(field);
      field.select();
      try { copied = document.execCommand("copy"); } catch { copied = false; }
      field.remove();
      button.focus({ preventScroll: true });
    }
    feedback.textContent = copied ? "Email address copied." : "Please select and copy the email address above.";
  });
}
function setupShareMetadata() {
  selectAll("[data-share-image]").forEach(meta => { meta.content = new URL(meta.getAttribute("content"), location.href).href; });
  const local = /^(localhost|127\.|10\.|192\.168\.|172\.(1[6-9]|2\d|3[01])\.|\[::1\])/i.test(location.hostname);
  if (!/^https?:$/.test(location.protocol) || local) return;
  const url = new URL(location.pathname, location.origin).href;
  const canonical = document.createElement("link");
  canonical.rel = "canonical";
  canonical.href = url;
  const ogUrl = document.createElement("meta");
  ogUrl.setAttribute("property", "og:url");
  ogUrl.content = url;
  document.head.append(canonical, ogUrl);
}
setupReveal();
setupHeaderOffset();
setupMobileMenu();
setupDemoExperience();
setupProductGallery();
setupMobileActionBar();
setupContact();
// Shopify renders canonical and sharing metadata in the layout.
selectAll("[data-action-link]").forEach(link => link.addEventListener("click", () => trackEvent("preview_action", { source: link.dataset.trackSource || "", destination: link.getAttribute("href") })));
refreshIcons();
